/*
    Copyright (C) 2023 - Wheel and Brake - Southwest Airlines
*/
import React, { useState, useEffect } from "react";
import UseTable from "./UseTable.js";
import TableFooter from "./TableFooter.js";
import { useNavigate } from "react-router-dom";
import "./Table.css";
import {create} from "../../../backend/create/Create";
import WnbModalSuccess from "../commons/WnbModalSuccess";
import WnbModalError from "../commons/WnbModalError";
import WnbModalLoading from "../commons/WnbModalLoading";
import { getAllActive } from "../../../backend/read/GetAllActive";
import {ACCESS_TOKEN_KEY} from "../../../utilities/Constants";

function multiSort(array, sortObject = {}) {
    const sortKeys = Object.keys(sortObject);

    // return array if no sort object is supplied.
    if (!sortKeys.length) {
        return array;
    }

    // change the values of the sortObject keys to -1, 0, or 1.
    for (let key in sortObject) {
        sortObject[key] = sortObject[key] === 'desc' || sortObject[key] === -1 ? -1 : (sortObject[key] === 'skip' ||
            sortObject[key] === 0 ? 0 : 1);
    }

    const keySort = (a, b, direction) => {
        direction = direction !== null ? direction : 1;
        if (a === b) {
            return 0;
        }
        return a > b ? direction : -1 * direction;
    };

    return array.sort((a, b) => {
        let sorted = 0;
        let index = 0;
        while (sorted === 0 && index < sortKeys.length) {
            const key = sortKeys[index];

            if (key) {
                const direction = sortObject[key];

                sorted = keySort(a[key], b[key], direction);
                index++;
            }
        }
        return sorted;
    });
}

const useSortableData = (items, config = null, secondConfig = ([])) => {
    // state to track which column is being sorted and how it's being sorted
    const [sortConfig, setSortConfig] = React.useState(config);
    const [filterConfig, setFilterConfig] = React.useState(secondConfig);

    // sort the data
    const filteredItems = React.useMemo(() => {
        let filterableItems = [...items];
        if (filterConfig.length > 0) {
            filterConfig.forEach(filters => {
                filterableItems = filterableItems.filter(item => {
                    if (item[filters.columnToFilter] == null) {
                        return 0;
                    }
                    if (filters.checkboxFilter) {
                        if (filters.columnToFilter === "ACTIVE_FLAG") {
                            let active = document.getElementById("ACTIVE").checked;
                            let inactive = document.getElementById("INACTIVE").checked;
                            if (active && inactive) {
                                return true;
                            } else if (active && !inactive) {
                                return item[filters.columnToFilter].toString() === "ACTIVE";
                            } else if (!active && inactive) {
                                return item[filters.columnToFilter].toString() === "INACTIVE";
                            } else {
                                return false;
                            }
                        } else {
                            let wheel = document.getElementById("WHEEL").checked;
                            let brake = document.getElementById("BRAKE").checked;
                            if (wheel && brake) {
                                return true;
                            } else if (wheel && !brake) {
                                return item[filters.columnToFilter].toString() === "WHEEL";
                            } else if (!wheel && brake) {
                                return item[filters.columnToFilter].toString() === "BRAKE";
                            } else {
                                return false;
                            }
                        }
                    }
                    return (
                        item[filters.columnToFilter].toString().toLowerCase()
                            .includes(filters.key.toString().toLowerCase())
                    );
                });
            });
            return filterableItems;
        }
    }, [items, filterConfig]);

    // #1 copy data to a temporary array

    // #2 if a sortConfig exists (a column has been clicked to be sorted) and its direction isn't
    // an empty string (set to default)

    // #3 if we are sorting on date column, create date object from string to do simple comparison

    // #4 for each item pair in the array, compare them and sort them accordingly
    const sortedItems = React.useMemo(() => {
        // #1
        let sortableItems;
        filterConfig.length > 0 ? sortableItems = [...filteredItems] : sortableItems = [...items];
        // #2
        if (sortConfig !== null && sortConfig.direction !== '') {
            sortableItems.sort((a, b) => {
                // #3
                let tempA;
                let tempB;
                if (sortConfig.key === "LAST_UPDATED_TIMESTAMP") {
                    tempA = new Date(b[sortConfig.key]);
                    tempB = new Date(a[sortConfig.key]);
                }
                else {
                    tempA = a[sortConfig.key];
                    tempB = b[sortConfig.key];
                }
                // #4
                if (tempA < tempB) {
                    if (typeof tempA == 'string' && typeof tempB == 'string') {
                        return sortConfig.direction === 'ascending' ? 1 : -1;
                    }
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (tempA > tempB) {
                    if (typeof tempA == 'string' && typeof tempB == 'string') {
                        return sortConfig.direction === 'ascending' ? -1 : 1;
                    }
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [items, sortConfig, filterConfig, filteredItems]);

    // function to sort a column when a column is clicked, the key is the name of the column

    // #1 set direction to ascending as if the column has been clicked for the first time

    // #2 check if sortConfig exists (a column has already been clicked to be sorted)
    // and if its key is matching to the column we just clicked

    // #3 if the column has already been clicked, and its direction is ascending, we set its direction
    // to descending

    // #4 else if its direction is descending, we set the direction to an empty string, which is just default

    // #5 then we call setSortConfig, to update the state of which column is being sorted and its direction
    const requestSort = (key) => {
        // #1
        let direction = 'ascending';
        // #2
        if (sortConfig && sortConfig.key === key) {
            // #3
            if (sortConfig.direction === 'ascending') {
                direction = 'descending';
            }
            // #4
            else if (sortConfig.direction === 'descending') {
                return setSortConfig(null);
            }
        }
        // #5
        setSortConfig({ key, direction });
    };

    const requestFilter = (columnToFilter, key, checkboxFilter) => {
        setFilterConfig((currentState) => {
            return [...currentState.filter((x) => x.columnToFilter !== columnToFilter),
            { columnToFilter: columnToFilter, key: key, checkboxFilter: checkboxFilter }];
        });
    };

    // if a sortConfig exists (a column has been clicked) and its direction is an empty string (default),
    // just return the original order of the data sent through, not the sorted data
    if (sortConfig) {
        return { items: sortedItems, requestSort, sortConfig, requestFilter };
    }
    else if (filterConfig.length > 0) {
        return { items: filteredItems, requestSort, sortConfig, requestFilter };
    }
    // else we returned the sorted data
    return { items: items, requestSort, sortConfig, requestFilter };
};

const Table = (props) => {
    multiSort(props.data, props.defaultSort);
    const navigate = useNavigate();
    const { items, requestSort, sortConfig, filterConfig, requestFilter } = useSortableData(props.data,
        { key: props.defaultSort.column, direction: props.defaultSort.direction });
    const [page, setPage] = React.useState(1);
    const { slice, range } = UseTable(items, page);

    const [showSaveAlert, setShowSaveAlert] = useState(false);
    const [saveMessage, setSaveMessage] = useState("");
    const [showNotSavedAlert, setShowNotSavedAlert] = useState(false);
    const [saveErrorMessage, setSaveErrorMessage] = useState("");
    const [isReprinting, setIsReprinting] = useState(false);

    const [partData, SetPartData] = useState([]);

    useEffect(() => { getAllActive("PARTS").then(res => SetPartData(JSON.parse(res.body.data).Items)) }, []);

    const onClickOkSaveSuccess = () => {
        setShowSaveAlert(false);
        window.location.reload();
    };

    const onClickOkSaveError = () => {
        setShowNotSavedAlert(false);
    };

    const handleTableButton = (item, name) => {

        console.log("ITEM: " + item.PART_ID);
        if(name === "Edit"){
            navigate(props.button.path, { state: item });
        } else {
            const part = partData.filter((el) => {
                return el.PART_ID === item.PART_ID
            })
            if(part.length < 1){
                throw new Error("Error retrieving PART information");
            }
            let body = {
                    "PART_ID": {
                        "S": item.PART_ID
                    },
                    "DESCRIPTION": {
                        "S": part[0].DESCRIPTION
                    },
                    "FTN_WO": {
                        "S": item.FTN_WO
                    },
                    "IB_SN": {
                        "S": item.IB_SN || ""
                    },
                    "OB_SN": {
                        "S": item.OB_SN || ""
                    },
                    "BRAKE_SN": {
                        "S": item.BRAKE_SN || ""
                    },
                    "PRINTER_ID": {
                        "S": item.PRINTER_ID
                    },
                    "STATION_CODE": {
                        "S": item.STATION_CODE
                    },
                    "EXPIRES": {
                        "S": item.EXPIRES || item.TAG_ID
                    },
                    "TAG_ID": {
                        "S": item.TAG_ID
                    },
                    "USER_ID": {
                        "S": item.USER_ID
                    },
                    // expiration date was added after the app went live, so not all tags have an exp date
                    // if the reprinted tag has an exp date, send it
                    // if not, send tag id (epoch timestamp) as exp date, backend will calculate based on that
                    "PRINT_TYPE": {
                        "S": item.EXPIRES ? "REPRINT_WITH_EXP" : "REPRINT_NO_EXP"
                    },
                    "ACCESS_TOKEN": {
                        "S": JSON.parse(sessionStorage.getItem(ACCESS_TOKEN_KEY)).accessToken
                    }
            };
            setIsReprinting(true);
            create("TAGS", body).then(resp => {
                setIsReprinting(false);
                if (resp.statusCode === 200) {
                    setShowSaveAlert(true);
                    setSaveMessage("Tag has been reprinted on printer \"" + item.PRINTER_ID + "\".");
                } else {
                    let errMsg = resp.body.message;  
                    setShowNotSavedAlert(true);
                    setSaveErrorMessage(errMsg);                    
                }
            }).catch(err => {
                setIsReprinting(false);
                setShowNotSavedAlert(true);
                setSaveErrorMessage("Failed to reprint tag. Please try again.");
            });
        }
    };

    return (
        <div>
            <div className="table-class">
                <table id='List'>
                    {/* Here we get the columns from the prop we passed in and iterate over it to create the columns
                    in the table */}
                    <thead>
                        <tr>
                            {props.cols.map((col) => (
                                <th>
                                    <div className="column-title">
                                        <h4 onClick={() => requestSort(col.attributeName)}>{col.name} {
                                            (sortConfig && sortConfig.key === col.attributeName) ?
                                                sortConfig.direction === 'descending' ? <span>&#8595;</span> :
                                                    <span>&#8593;</span> : ''} </h4>
                                    </div>
                                    {col.filter ?
                                        col.checkboxFilter ?
                                            <div className="filter-container">
                                                <div className="checkboxFilter-container">
                                                    <div className="checkbox-container">
                                                        <input
                                                            type="checkbox"
                                                            className={"check-box"}
                                                            defaultChecked={true}
                                                            name={col.attributeName}
                                                            id={col.checkboxTitleOne}
                                                            value={filterConfig}
                                                            onChange={() => requestFilter(col.attributeName,
                                                                col.checkboxTitleOne, col.checkboxFilter)}
                                                        />
                                                        <label className = "label" htmlFor={col.checkboxTitleOne}>
                                                            {col.checkboxTitleOne}</label>
                                                    </div>
                                                    
                                                    <div className="checkbox-container">

                                                        <input
                                                            type="checkbox"
                                                            className={"check-box"}
                                                            defaultChecked={true}
                                                            name={col.attributeName}
                                                            id={col.checkboxTitleTwo}
                                                            value={filterConfig}
                                                            onChange={() => requestFilter(col.attributeName,
                                                                col.checkboxTitleTwo, col.checkboxFilter)}
                                                        />
                                                        <label className = "label" htmlFor={col.checkboxTitleTwo}>
                                                            {col.checkboxTitleTwo}</label>
                                                    </div>
                                                </div>
                                            </div>
                                            :
                                            <div style={{ "padding": "10px 0px 0px" }}>
                                                <input
                                                    type="search"
                                                    style={{ "width": "95%" }}
                                                    value={filterConfig}
                                                    onInput={e => requestFilter(col.attributeName, e.target.value, false)}
                                                />
                                            </div>
                                        :
                                        <div></div>
                                    }
                                </th>
                            ))}<th style={{ "width": "8%" }} hidden={props.button.hide}></th></tr>
                    </thead>
                    <tbody>
                        {/* Here we iterate over each object from the passed data to create a row for each one */}
                        {slice.map((item) => (
                            <tr>
                                {/* Then we iterate over the columns, also the properties of each object and load that as
                            a column into the table */}
                                {props.cols.map((col) => (
                                    <td>{item[col.attributeName]}</td>
                                ))}
                                <td hidden={props.button.hide}><button onClick={() => handleTableButton(item, props.button.name)}>{props.button.name}</button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <WnbModalSuccess
                    title="Success"
                    message={saveMessage}
                    handleOk={onClickOkSaveSuccess}
                    handleNew={onClickOkSaveSuccess}
                    showAlert={showSaveAlert}
                    successType="TAG"
                    createLabel="OK"
                />
                <WnbModalLoading
                    showAlert={isReprinting}
                    message="Sending tag to printer..."
                />
                <WnbModalError
                    title="Error"
                    message={saveErrorMessage}
                    handleOk={onClickOkSaveError}
                    showAlert={showNotSavedAlert}
                />
            </div>
            <TableFooter range={range} slice={slice} setPage={setPage} page={page} total={items.length} />
        </div>
    );
};

export default Table;
